import React, {
    forwardRef, useEffect, useImperativeHandle, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import CategoryItem from '../../../component/dump/menuComponents/CategoryItem';
import momenttz from '../../../component/helpers/momenttz';
import { getFleetCameraHealth } from '../../../../../shared/api/action/ReportsActions';
import { cameraHealthIssueTypes, CAMERA_HEALTH_ISSUE_GROUPS } from '../../../component/helpers/cameraHealthIssues';
import CameraHealthAssetsModal from './CameraHealthAssetsModal';

const GROUP_ORDER = [
    CAMERA_HEALTH_ISSUE_GROUPS.deviceHealthStatus,
    CAMERA_HEALTH_ISSUE_GROUPS.sdWearLevel,
    CAMERA_HEALTH_ISSUE_GROUPS.cameraMisalignment,
    CAMERA_HEALTH_ISSUE_GROUPS.cameraBlock,
];

const getCameraHealthRange = () => ({
    start_ts: momenttz(moment().subtract(6, 'days').format('YYYY-MM-DD 00:00:00')).utc().format('YYYYMMDDHHmmss'),
    end_ts: momenttz(moment().format('YYYY-MM-DD 23:59:59')).utc().format('YYYYMMDDHHmmss'),
});

const FleetCameraHealthTile = forwardRef((props, ref) => {
    const {
        companyId, apiRef, className, style, onMouseDown, onMouseUp, onTouchStart, onTouchEnd, customizeControls,
    } = props;
    const { t } = useTranslation('translation', { keyPrefix: 'dashboardPage.fleetCameraHealth' });
    const [devices, setDevices] = useState([]);
    const [selectedIssueKey, setSelectedIssueKey] = useState(null);
    const requestIdRef = useRef(0);

    const loadDevices = () => {
        if (!companyId) return;
        requestIdRef.current += 1;
        const requestId = requestIdRef.current;
        const isLatest = () => requestIdRef.current === requestId;
        const { start_ts, end_ts } = getCameraHealthRange();
        getFleetCameraHealth(companyId, start_ts, end_ts)
            .then((res) => isLatest() && setDevices(res.data.response.devices || []))
            .catch(() => isLatest() && setDevices([]));
    };

    useEffect(() => {
        loadDevices();
        return () => {
            requestIdRef.current += 1;
        };
    }, [companyId]);

    useImperativeHandle(apiRef, () => ({ reload: loadDevices }));

    const issueTypes = cameraHealthIssueTypes();
    const issueCounts = issueTypes.reduce((result, issue) => {
        result[issue.key] = devices.filter((device) => device.health?.[issue.key]).length;
        return result;
    }, {});
    const totalAffected = devices.length;

    const selectedIssue = issueTypes.find((issue) => issue.key === selectedIssueKey) || null;
    const selectedIssueDevices = selectedIssue
        ? devices.filter((device) => device.health?.[selectedIssue.key])
        : [];

    const groups = GROUP_ORDER
        .map((group) => ({
            group,
            issues: issueTypes.filter((issue) => issue.group === group && issueCounts[issue.key] > 0),
        }))
        .filter(({ issues }) => issues.length > 0);

    return (
        <div
            ref={ref}
            className={classNames('tile', 'tile--camera-health', className)}
            data-testid="fleet-camera-health-tile"
            style={style}
            onMouseDown={onMouseDown}
            onMouseUp={onMouseUp}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
        >
            {customizeControls}
            <div className="tile__hd">
                <span className="tile__title">{t('title')}</span>
                {totalAffected > 0 &&
                    <span className="tile__badge tile__badge--danger">
                        {t('badgeDevicesAffected', { count: totalAffected })}
                    </span>
                }
            </div>
            <div className="tile__bd">
                {groups.length === 0 && <div className="camera-health-empty">{t('textNoIssues')}</div>}
                {groups.map(({ group, issues }) => (
                    <div key={group} className="camera-health-group">
                        <h6 className="camera-health-group__title">{t(`groups.${group}`)}</h6>
                        {issues.map((issue) => (
                            <CategoryItem
                                key={issue.key}
                                text={issue.title}
                                rightLabel={issueCounts[issue.key]}
                                withArrow
                                arrowOnly
                                arrowLabel={t('ariaViewDetails')}
                                icon={issue.icon}
                                className={`camera-health-row camera-health-row--${issue.severity}`}
                                onClick={() => setSelectedIssueKey(issue.key)}
                            />
                        ))}
                    </div>
                ))}
            </div>
            {selectedIssue &&
                <CameraHealthAssetsModal
                    issue={selectedIssue}
                    devices={selectedIssueDevices}
                    onHide={() => setSelectedIssueKey(null)}
                />
            }
        </div>
    );
});

FleetCameraHealthTile.displayName = 'FleetCameraHealthTile';

FleetCameraHealthTile.propTypes = {
    companyId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    apiRef: PropTypes.shape({ current: PropTypes.object }),
    className: PropTypes.string,
    style: PropTypes.object,
    onMouseDown: PropTypes.func,
    onMouseUp: PropTypes.func,
    onTouchStart: PropTypes.func,
    onTouchEnd: PropTypes.func,
    customizeControls: PropTypes.node,
};

FleetCameraHealthTile.defaultProps = {
    apiRef: undefined,
    className: undefined,
    style: undefined,
    onMouseDown: undefined,
    onMouseUp: undefined,
    onTouchStart: undefined,
    onTouchEnd: undefined,
    customizeControls: null,
};

export default FleetCameraHealthTile;
